import { Breadcrumbs } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { StyledLink } from "../../CommonUI/BuildlingBlocks/StyledLink";
import { StyledButton } from "../../CommonUI/BuildlingBlocks/StyledButton";
import { ExpressionRule } from "../../rule-engine-core/Expressions/ExpressionRule";
import { ExpressionValue } from "../../rule-engine-core/Expressions/ExpressionValue";
import { ExpressionConfiguration } from "../../rule-engine-core/Expressions/ExpressionConfiguration";
import { ExpressionContext } from "../../rule-engine-core/Expressions/ExpressionContext";
import {
    ExpressionRuleRegistration,
    ExpressionRuleRegistrationData,
} from "../../rule-engine-core/Expressions/RegisterExpressionRule";
import { ToolboxPanel } from "./ToolboxPanel";
import { GetExpressionRuleIcon, RenderExpressionRule } from "./RegisterExpressionRuleEditor";
import { UIStyleConstants } from "../../rule-engine-core/Common/UIStyleConstants";

export const InsertExpressionPage = function (props: {
    expressionValue: ExpressionValue;
    onComplete: (position: ExpressionValue, inserted: ExpressionRule, selected: string) => void;
    onCancel: () => void;
}) {
    const [selectedExpression, setSelectedExpression] = useState<ExpressionRule | undefined>(undefined);

    const [posExpressionValue, setPosExpressionValue] = useState<ExpressionValue>(props.expressionValue);

    const [step2, setStep2] = useState(false);

    const handleSelection = useCallback(
        (selected: string) => {
            if (selectedExpression) {
                props.onComplete(posExpressionValue, selectedExpression, selected);
            }
        },
        [selectedExpression, props, posExpressionValue],
    );

    function onExpressionSelected(registration: ExpressionRuleRegistrationData) {
        let exp = registration.create();

        let context = new ExpressionContext(new ExpressionConfiguration());

        context.emptyExpressionText = "Select";
        exp.setEditContextWithoutRegister(context);
        setSelectedExpression(exp);
        setStep2(true);
    }

    useEffect(() => {
        if (selectedExpression && selectedExpression.expressionContext) {
            selectedExpression.expressionContext.onSelection = handleSelection;
        }
    }, [selectedExpression, handleSelection]);

    if (!step2) {
        return (
            <InsertExpressionViewPanel>
                <InsertExpressionViewPanelLine margin="0 0 10px">
                    Insert Expression - step 1, select expression
                </InsertExpressionViewPanelLine>
                {
                    <ToolboxPanel
                        large={false}
                        expressionValue={props.expressionValue}
                        onExpressionSelected={onExpressionSelected}
                    />
                }
            </InsertExpressionViewPanel>
        );
    } else {
        let type = Reflect.get(selectedExpression!, "__type");
        const expression = ExpressionRuleRegistration.getExpressionByType(
            type,
            props.expressionValue.editContext!.expression.getInputDefinition(),
        );
        let parents = props.expressionValue?.editContext?.getParents(props.expressionValue._id).reverse() || [];
        let expressions: ExpressionRule[] = [];
        let values: ExpressionValue[] = [];

        parents.forEach((p) => {
            if (p instanceof ExpressionValue) {
                values.push(p);
            } else {
                expressions.push(p as ExpressionRule);
            }
        });
        return (
            <InsertExpressionViewPanel>
                <InsertExpressionViewPanelLine margin="0 0 10px">Inserting</InsertExpressionViewPanelLine>
                <InsertExpressionViewPanelLine margin="0 0 10px">
                    <StyledButton
                        size="small"
                        startIcon={GetExpressionRuleIcon(expression!.type)}
                        variant="outlined"
                        onClick={() => {
                            setStep2(false);
                            setSelectedExpression(undefined);
                        }}
                    >
                        {expression?.name}
                    </StyledButton>
                </InsertExpressionViewPanelLine>
                <InsertExpressionViewPanelLine>
                    <Breadcrumbs separator=">">
                        {expressions.map((e, idx) => (
                            <StyledLink
                                key={idx}
                                onClick={() => {
                                    setPosExpressionValue(values[idx]!);
                                }}
                                size="small"
                            >
                                {e.__type}
                            </StyledLink>
                        ))}
                    </Breadcrumbs>
                </InsertExpressionViewPanelLine>
                <InsertExpressionViewPanelLineCenter>
                    {RenderExpressionRule(selectedExpression)}
                </InsertExpressionViewPanelLineCenter>
                <InsertExpressionViewPanelLine>
                    <StyledLink
                        size="small"
                        onClick={props.onCancel}
                    >
                        Cancel Insertion
                    </StyledLink>
                </InsertExpressionViewPanelLine>
            </InsertExpressionViewPanel>
        );
    }
};

const InsertExpressionViewPanelLineCenter = styled.div`
    display: flex;
    margin-left: auto;
    justify-content: center;
    align-items: center;
    min-height: 150px;
    padding: 15px;
    border-radius: 5px;
    flex: 1;
    overflow: auto;
    width: 100%;
`;

const InsertExpressionViewPanel = styled.div`
    width: 100%;
    max-height: 376px;
    min-height: 96px;
    padding: 20px 0;
    clear: both;
    display: flex;
    flex-direction: column;
`;

const InsertExpressionViewPanelLine = styled.div<{ margin?: string }>`
    width: 100%;
    justify-content: flex-start;
    clear: both;

    font-style: normal;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${UIStyleConstants.TEXT_COLOR_DEFAULT};
    text-transform: none;

    margin: ${(props) => props.margin || "0px"};
`;
